import { ref, computed, watch } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import useFilterCounter from "@/components/Filters/useFiltersCount";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { debounce } from "lodash";
import moment from "moment-timezone";

export default function useList() {
  const { t } = useI18nUtils();
  const trans = {
    "messages.error.server_error": t("messages.error.server_error"),
  };

  const refListTable = ref(null);

  const toast = useToast();

  store.dispatch("unit_commander/getAll");
  const commanderOptions = computed(
    () => store.getters["unit_commander/getSelectedItems"]
  );

  const activeOptions = ref([
    { value: true, label: t("status_active") },
    { value: false, label: t("status_inactive") },
  ]);

  // ---- Table ----
  const tableColumns = [
    { label: "actions.name", key: "actions", sortable: false },
    { label: "patrol_logs.unit_log.unit", key: "unit_id", sortable: false },
    {
      label: "patrol_logs.unit_log.telephone_number",
      key: "telephone_number",
      sortable: false,
    },
    {
      label: "patrol_logs.unit_log.router_number",
      key: "router_number",
      sortable: false,
    },
    { label: 'patrol_logs.patrol_log.command_officer', key: 'unit_commander.name', sortable: true },
    { label: 'patrol_logs.unit_commander.telephone_number', key: 'unit_commander.telephone_number', sortable: true },
    { label: 'patrol_logs.patrol_log.patrol_officer', key: 'patrol_office.name', sortable: true },
    { label: 'patrol_logs.unit_commander.telephone_number', key: 'patrol_office.telephone_number', sortable: true },
    {
      label: "date.created_at",
      key: "created_at",
      sortable: true,
      formatter: "formatDateAssigned",
    },
  ];

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);

  const formatDateAssigned = (value) => {
    const formattedDate = moment(value).format("DD/MM/YYYY HH:mm:ss");
    return formattedDate;
  };

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  // ---- Filters ----
  const unitFilter = ref(null);
  const activeFilter = ref(null);
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);

  const buildListQueryParams = () => ({
    q: searchQuery.value,
    max: perPage.value,
    "page[n]": currentPage.value,
    sortBy: sortBy.value,
    sortDesc: isSortDirDesc.value,
    unit: unitFilter.value,
    active: activeFilter.value,
    with: ["unit", "unit_commander"],
  });

  const fetchList = (ctx, callback) => {
    store
      .dispatch("unit_log/getAll", buildListQueryParams())
      .then((response) => {
        callback(response.data);
        totalList.value = response.meta.total;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: trans["messages.error.server_error"],
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const refetchData = () => {
    refListTable.value.refresh();
  };

  watch([currentPage, perPage], () => {
    refetchData();
  });

  watch(
    [searchQuery],
    debounce(() => {
      refetchData();
    }, 500)
  );

  const buildFilters = () => {
    filters.value = {
      unit: unitFilter.value,
      active: activeFilter.value,
    };
    refetchData();
  };

  const clearSearchFilters = () => {
    unitFilter.value = null;
    activeFilter.value = null;
    filters.value = {};
    buildFilters();
  };

  return {
    refListTable,
    tableColumns,
    perPage,
    currentPage,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    totalList,
    dataMeta,
    fetchList,
    refetchData,

    // options
    commanderOptions,
    activeOptions,

    formatDateAssigned,

    // filters
    unitFilter,
    activeFilter,
    filtersCount,
    buildFilters,
    clearSearchFilters,
    buildListQueryParams,
  };
}
